import AddIcon from "@atlaskit/icon/core/add";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import {token} from "@atlaskit/tokens";
import {useNavigate} from "@tanstack/react-router";
import {useState} from "react";
import {BottomDrawer} from "~/components/BottomDrawer";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {Workspace} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/Workspace";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {isFormPage, type Form, type WorkspaceConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {configCtaDisplayLabel} from "~/workspaceConfig/configCtaActionIcons";
import {isHeaderCreateFormAccessible, resolveHeaderCreateMenuCtas} from "~/workspaceConfig/configHeaderCreateDropdown";
import {resolveConfigFormHref} from "~/workspaceConfig/configHref";
import {hasWorkspaceUiConfig, loadOrCreateWorkspaceConfig} from "~/workspaceConfig/configRuntime";

export type EventContainerAddEventItem = {
    label: string;
    href: string;
};

function AddEventOptionRowButton({label, onClick}: {label: string; onClick: () => void}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: token("space.150", "12px"),
                height: "40px",
                minHeight: "32px",
                padding: `0 ${token("space.150", "12px")}`,
                borderRadius: "4px",
                background: token("color.background.input"),
                color: token("color.text"),
                cursor: "pointer",
            }}
        >
            <AnankeText color="color.text.subtle">{label}</AnankeText>
            <ChevronRightIcon label="" color={token("color.icon.subtle")} />
        </button>
    );
}

const ADD_EVENT_FORM_MODES = new Set(["create", "log", "schedule", "book"]);

function isJourneyAddEventForm(form: Form): boolean {
    if (form.targetEntity === "eventContainer") {
        return false;
    }
    return ADD_EVENT_FORM_MODES.has(form.mode);
}

function addEventItem(
    workspaceId: Uuid,
    label: string,
    formId: Uuid,
    eventContainerId: Uuid,
): EventContainerAddEventItem | null {
    const href = resolveConfigFormHref({
        workspaceId,
        formId,
        search: {eventContainerId},
    });
    if (href === null) {
        return null;
    }
    return {label, href};
}

function pushAddEventItem(
    items: Array<EventContainerAddEventItem>,
    seenFormIds: Set<string>,
    item: EventContainerAddEventItem | null,
    formId: Uuid,
): void {
    if (item === null || seenFormIds.has(formId) === true) {
        return;
    }
    seenFormIds.add(formId);
    items.push(item);
}

function addEventItemsFromConfig(
    config: WorkspaceConfig,
    workspaceId: Uuid,
    eventContainerId: Uuid,
    userTemplateId: Uuid | undefined,
): Array<EventContainerAddEventItem> {
    const items: Array<EventContainerAddEventItem> = [];
    const seenFormIds = new Set<string>();

    for (const cta of resolveHeaderCreateMenuCtas(config, userTemplateId)) {
        if (cta.action.type !== "openForm") {
            continue;
        }
        const form = config.forms[cta.action.formId];
        if (form === undefined || isJourneyAddEventForm(form) !== true) {
            continue;
        }
        pushAddEventItem(
            items,
            seenFormIds,
            addEventItem(workspaceId, configCtaDisplayLabel(cta), form.id, eventContainerId),
            form.id,
        );
    }

    for (const page of Object.values(config.pages)) {
        if (isFormPage(page) !== true) {
            continue;
        }
        const form = config.forms[page.formId];
        if (form === undefined || isJourneyAddEventForm(form) !== true) {
            continue;
        }
        if (isHeaderCreateFormAccessible(config, form.id, userTemplateId) !== true) {
            continue;
        }
        pushAddEventItem(
            items,
            seenFormIds,
            addEventItem(workspaceId, form.title, form.id, eventContainerId),
            form.id,
        );
    }

    return items;
}

export function getEventContainerAddEventItems({
    workspace,
    eventContainerId,
    userTemplateId,
}: {
    workspace: Workspace;
    workspaceCapabilities?: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    eventContainerId: Uuid;
    userTemplateId?: Uuid;
}): Array<EventContainerAddEventItem> {
    if (hasWorkspaceUiConfig(workspace.id) !== true) {
        return [];
    }
    return addEventItemsFromConfig(
        loadOrCreateWorkspaceConfig(workspace.id),
        workspace.id,
        eventContainerId,
        userTemplateId,
    );
}

function EventContainerAddEventDropdownItems({items}: {items: Array<EventContainerAddEventItem>}) {
    return (
        <AnankeDropdownItemGroup>
            {items.map((item) => (
                <AnankeDropdownItem key={item.label} href={item.href}>
                    {item.label}
                </AnankeDropdownItem>
            ))}
        </AnankeDropdownItemGroup>
    );
}

export function EventContainerAddEventButton({items}: {items: Array<EventContainerAddEventItem>}) {
    const navigate = useNavigate();
    const [opened, setOpened] = useState(false);

    if (items.length === 0) {
        return null;
    }

    return (
        <>
            <div className="hidden sm:block">
                <AnankeDropdownMenu trigger="+ Add event">
                    <EventContainerAddEventDropdownItems items={items} />
                </AnankeDropdownMenu>
            </div>
            <div className="block sm:hidden">
                <button
                    type="button"
                    aria-label="Add event"
                    className="flex h-[32px] w-[32px] items-center justify-center rounded-[4px] border-none"
                    style={{
                        backgroundColor: "#1868DB",
                        color: "#FFFFFF",
                        cursor: "pointer",
                    }}
                    onClick={() => {
                        setOpened(true);
                    }}
                >
                    <AddIcon label="" size="small" />
                </button>
                <BottomDrawer
                    open={opened}
                    setOpen={setOpened}
                    title="Add event"
                    zIndex={100}
                    fitContentOnOpen={false}
                    initialOpenHeight={480}
                >
                    <div className="grid gap-ds-space-100" style={{fontFamily: "Inter"}}>
                        {items.map((item) => (
                            <AddEventOptionRowButton
                                key={item.label}
                                label={item.label}
                                onClick={() => {
                                    void navigate({to: item.href});
                                    setOpened(false);
                                }}
                            />
                        ))}
                    </div>
                </BottomDrawer>
            </div>
        </>
    );
}
